"use client";

import {
  AlertCircle,
  Bell,
  Check,
  Info,
  LoaderCircle,
  X,
  type LucideIcon,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { type ReactNode } from "react";
import { EASE_OUT } from "../../lib/ease";
import { cn } from "../../lib/utils";

export type ToastStatus = "neutral" | "info" | "loading" | "success" | "error";

export type ToastAction = {
  label: ReactNode;
  onClick: (toast: ToastData) => void;
};

export type ToastData = {
  id: string;
  title: ReactNode;
  description?: ReactNode;
  status?: ToastStatus;
  icon?: ReactNode;
  action?: ToastAction;
  duration?: number;
  dismissible?: boolean;
  createdAt?: number;
};

export type ToastClassNames = {
  surface?: string;
  iconWrap?: string;
  content?: string;
  title?: string;
  description?: string;
  action?: string;
  close?: string;
};

const CONTENT_TRANSITION = {
  duration: 0.28,
  ease: EASE_OUT,
} as const;

const STATUS_ICON: Record<ToastStatus, LucideIcon> = {
  neutral: Bell,
  info: Info,
  loading: LoaderCircle,
  success: Check,
  error: AlertCircle,
};

const STATUS_CLASS: Record<ToastStatus, string> = {
  neutral: "text-v2-muted-foreground bg-v2-primary/[0.05]",
  info: "text-v2-primary bg-v2-primary/10",
  loading: "text-v2-primary bg-v2-primary/10",
  success: "text-v2-success-text bg-v2-success/10",
  error: "text-v2-destructive-text bg-v2-destructive/10",
};

export interface ToastProps {
  toast: ToastData;
  onDismiss?: (id: string) => void;
  classNames?: ToastClassNames;
  icons?: Partial<Record<ToastStatus, ReactNode>>;
  renderToast?: (toast: ToastData) => ReactNode;
  className?: string;
}

/**
 * A single toast surface: status icon, title, optional description, optional
 * action, and a dismiss button. Rendered on its own or by `ToastStack`.
 *
 * @param props - The toast data plus dismiss handler, class overrides, and a
 * `renderToast` escape hatch for fully custom content.
 *
 * @returns The toast surface element.
 */
export function Toast({
  toast,
  onDismiss,
  classNames,
  icons,
  renderToast,
  className,
}: ToastProps) {
  const reduce = useReducedMotion();
  const status = toast.status ?? "neutral";
  const Icon = STATUS_ICON[status];
  const iconNode =
    icons?.[status] ?? toast.icon ?? <Icon className="h-3.5 w-3.5" />;
  const canDismiss = toast.dismissible !== false && Boolean(onDismiss);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-v2-xl border border-v2-border bg-v2-popover/95 p-3 shadow-v2-modal backdrop-blur-xl",
        classNames?.surface,
        className,
      )}
    >
      {renderToast ? (
        renderToast(toast)
      ) : (
        <div className={cn("flex gap-3", toast.description ? "items-start" : "items-center")}>
          <motion.span
            layout
            className={cn(
              "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
              toast.description && "mt-0.5",
              STATUS_CLASS[status],
              classNames?.iconWrap,
            )}
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={status}
                initial={
                  reduce
                    ? { opacity: 0 }
                    : { opacity: 0, y: 8, scale: 0.8, filter: "blur(6px)" }
                }
                animate={
                  reduce
                    ? { opacity: 1 }
                    : { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }
                }
                exit={
                  reduce
                    ? { opacity: 0 }
                    : { opacity: 0, y: -8, scale: 0.9, filter: "blur(6px)" }
                }
                transition={CONTENT_TRANSITION}
                className="inline-flex"
              >
                {status === "loading" ? (
                  <span className="inline-flex animate-spin">{iconNode}</span>
                ) : (
                  iconNode
                )}
              </motion.span>
            </AnimatePresence>
          </motion.span>

          <div className={cn("min-w-0 flex-1", classNames?.content)}>
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={`${toast.id}-${status}-${String(toast.title)}`}
                initial={
                  reduce ? { opacity: 0 } : { opacity: 0, y: 8, filter: "blur(6px)" }
                }
                animate={
                  reduce ? { opacity: 1 } : { opacity: 1, y: 0, filter: "blur(0px)" }
                }
                exit={
                  reduce ? { opacity: 0 } : { opacity: 0, y: -8, filter: "blur(6px)" }
                }
                transition={CONTENT_TRANSITION}
              >
                <p
                  className={cn(
                    "truncate text-v2-base font-medium text-v2-foreground",
                    classNames?.title,
                  )}
                >
                  {toast.title}
                </p>
                {toast.description ? (
                  <p
                    className={cn(
                      "mt-0.5 line-clamp-2 text-v2-xs text-v2-muted-foreground",
                      classNames?.description,
                    )}
                  >
                    {toast.description}
                  </p>
                ) : null}
              </motion.div>
            </AnimatePresence>

            {toast.action ? (
              <button
                type="button"
                onClick={() => toast.action?.onClick(toast)}
                className={cn(
                  "mt-2 inline-flex h-7 items-center rounded-v2-md bg-v2-primary/[0.06] px-3 text-v2-xs font-medium text-v2-foreground transition-colors hover:bg-v2-primary/[0.1]",
                  classNames?.action,
                )}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>

          {canDismiss ? (
            <button
              type="button"
              onClick={() => onDismiss?.(toast.id)}
              aria-label="Dismiss toast"
              className={cn(
                "inline-flex size-7 shrink-0 items-center justify-center rounded-v2-md text-v2-muted-foreground transition-colors hover:bg-v2-primary/[0.06] hover:text-v2-foreground",
                classNames?.close,
              )}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
